/**
 * translateAPI: 번역 API 및 크레딧 관련
 * 원본: wehago-conference/src/utils/apiV2/index.js의 translateOpenAI
 */
import axios from 'axios';
import { apiCore } from './apiCore';
import apiConfig from './config';
import { getCookie } from '@/utils/cookie';
import type {
  TranslateRequest,
  TranslateResponse,
  UseCreditRequest,
  UseCreditResponse,
  CreditInfo,
  GetCreditResponse,
} from '@/types/api';

// re-export for backwards compatibility
export type {
  TranslateRequest,
  TranslateResponse,
  TranslateResponseData,
  UseCreditRequest,
  UseCreditResponse,
  CreditInfo,
  GetCreditResponse,
} from '@/types/api';


/**
 * 인증/비인증 분기 헬퍼
 * 원본: wehago-conference/src/utils/apiV2/index.js 공통 패턴
 * - AUTH_A_TOKEN 쿠키가 있으면 인증(apip/baseApiUrl) URL + 인증 헤더
 * - 그 외(게스트)면 비인증(api0/baseGuestUrl) URL + signature 헤더
 *
 * 주의: import.meta.env.DEV 를 인증 판단 조건에 넣으면
 *       개발기 게스트가 api0(rtctest.wehago.com/api0) 대신
 *       인증 URL로 빠져서 실패한다. 실제 토큰 유무로만 판단.
 */
const isAuthenticated = (): boolean =>
  !!(getCookie('AUTH_A_TOKEN'));

const getAuthHeaders = async (
  endPoint: string,
): Promise<{ url: string; headers: Record<string, string | number | boolean> }> => {
  const checkAuth = isAuthenticated();

  let url: string;
  let signaturePath = endPoint;

  if (checkAuth) {
    // 인증: baseApiUrl + endPoint
    // 예: https://rtctest.wehago.com/api-bind/open-ai/translate
    url = `${apiConfig.weMeetAPI.baseApiUrl}${endPoint}`;
  } else {
    // 비인증: baseGuestVideoConferenceUrl + subPath
    // 예(dev):  https://api0.wehago.com/videodev/open-ai/translate
    // 예(prod): https://api0.wehago.com/video/open-ai/translate
    const subPath = endPoint.replace(apiConfig.weMeetAPI.endpoint, '');
    url = `${apiConfig.baseGuestVideoConferenceUrl}${subPath}`;
    // signature: get_token?url=/videodev/open-ai/translate (실제 호출 경로와 일치)
    signaturePath = `/${apiConfig.guestEndpoint}${subPath}`;
  }

  let headers: Record<string, string | number | boolean> = {};
  if (checkAuth) {
    headers = apiCore.getRequestHeaders(url);
  } else {
    const tokenBase = new URL(apiConfig.baseGuestVideoConferenceUrl).origin;
    const signature = await apiCore.getUnAuthSignature(signaturePath, tokenBase);
    headers = { signature };
  }
  return { url, headers };
};

// ─── 번역 API ────────────────────────────────────────────────────────────

/**
 * 회의 번역 API (v2)
 * POST /v2/chat/translate
 *
 * @param req.input - 번역할 텍스트
 * @param req.fromLang - 원본 언어 (예: 'ko', 'en')
 * @param req.toLang - 대상 언어 (예: 'en', 'ja')
 * @param req.consumptionType - MASTER: 서버 즉시 차감 / USER: 차감 없이 토큰 정보만
 * @param req.roomId - MASTER 모드 시 필수
 * @param req.sttDictionary - STT 오인식 보정 키워드 (최대 1024자)
 */
export const translateOpenAI = async (
  req: TranslateRequest
): Promise<TranslateResponse> => {
  const { input, fromLang, toLang, consumptionType, roomId, sttDictionary = '' } = req;

  const trimmedDict =
    sttDictionary.length > 1024 ? sttDictionary.substring(0, 1024) : sttDictionary;

  const endPoint = `${apiConfig.weMeetAPI.endpoint}/v2/chat/translate`;
  const { url, headers } = await getAuthHeaders(endPoint);

  const body: Record<string, string> = {
    input,
    fromLang,
    toLang,
    consumptionType,
  };
  if (consumptionType === 'MASTER' && roomId) {
    body.roomId = roomId;
  }
  if (trimmedDict) {
    body.sttDictionary = trimmedDict;
  }

  const { data } = await axios({
    headers,
    method: 'POST',
    url,
    data: body,
  });

  return data as TranslateResponse;
};

// ─── 크레딧 차감 API ─────────────────────────────────────────────────────

/**
 * ONE AI 크레딧 차감 API
 * POST /one-ai/use/credit
 * @see http://wiki.duzon.com:8080/pages/viewpage.action?pageId=237872933
 */
export const useOneAiCredit = async (
  req: UseCreditRequest
): Promise<UseCreditResponse> => {
  const endPoint = `${apiConfig.weMeetAPI.endpoint}/one-ai/use/credit`;
  const { url, headers } = await getAuthHeaders(endPoint);

  const { data } = await axios({
    headers,
    method: 'POST',
    url,
    data: req,
  });

  return data as UseCreditResponse;
};

// ─── 크레딧 조회 API ─────────────────────────────────────────────────────

/**
 * 세션 사용자 크레딧 조회 (v2)
 * GET /v2/chat/credit/me
 * 인증 필수 (Bearer Token)
 */
export const getMyCredit = async (): Promise<GetCreditResponse | null> => {
  const cno = apiCore.hSelectedCno;
  const queryParams = new URLSearchParams();
  if (cno) queryParams.set('cno', cno);

  const queryString = queryParams.toString();
  const endPoint = `${apiConfig.weMeetAPI.endpoint}/v2/chat/credit/me${queryString ? `?${queryString}` : ''}`;
  const { url, headers } = await getAuthHeaders(endPoint);

  try {
    const { data } = await axios({ headers, url, method: 'GET' });
    if (data.resultCode === 200) {
      return data as GetCreditResponse;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * 방 개설자 크레딧 조회 (v2)
 * GET /v2/chat/credit/room?roomId=xxx
 * 인증 불필요 (회원/비회원 공용)
 */
export const getRoomCredit = async (
  roomId: string
): Promise<GetCreditResponse | null> => {
  const endPoint = `${apiConfig.weMeetAPI.endpoint}/v2/chat/credit/room?roomId=${encodeURIComponent(roomId)}`;
  const { url, headers } = await getAuthHeaders(endPoint);

  try {
    const { data } = await axios({ headers, url, method: 'GET' });
    if (data.resultCode === 200) {
      return data as GetCreditResponse;
    }
    return null;
  } catch {
    return null;
  }
};
